import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { CustomToast } from '../toast/CustomToast';
import {
  Text,
  Flex,
  Input,
  Box,
  Avatar,
  useDisclosure,
} from '@chakra-ui/react';
import BrandButton from '../brandButton/BrandButton';
import { mutateFunction } from '../../libs/apis';
import { UserContext } from '../../context/UserContext';
import AutoResizeTextarea from './AutoResizeTextArea';
import { TbLink, TbPhoto, TbCode } from 'react-icons/tb';
import { CloseIcon } from '@chakra-ui/icons';
import EmbedVideoLinkModal from '../modal/EmbedVideoLinkModal';
import EmbedHtmlCodeModal from '../modal/EmbedHtmlCodeModal';
import MediaDisplay from './MediaDisplay';
import {
  sanitizeHtml,
  isValidEmbedCode,
  makeEmbedResponsive,
} from '../../utils/sanitizeHtml';
import useData from '../../data/useData';
import UserTaggingTextarea from './UserTaggingTextarea';
import { extractVideoLinks } from '../../utils/validateVideoLinks';
import { BsCameraVideo } from 'react-icons/bs';

const CreatePost = ({
  onClose = () => {},
  showCloseButton = true,
  appendToPostList = (data) => {},
  postId = null,
  refetchFeed = () => {},
}) => {
  const { mutate, isLoading } = useMutation(mutateFunction);
  const { addToast } = CustomToast();
  const [postMessage, setPostMessage] = useState('');
  const [link, setLink] = useState('');
  const { userInformation } = useContext(UserContext);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const imagesInputRef = useRef(null);
  const [embedCode, setEmbedCode] = useState('');
  const [isActive, setIsActive] = useState(false);
  const maxPostLength = 400;
  const [autoVideos, setAutoVideos] = useState([]);
  const [textareaKey, setTextareaKey] = useState(0);
  const [mentions, setMentions] = useState([]);
  const postAreaRef = useRef(null);

  const {
    isOpen: createEmbedVideoIsOpen,
    onClose: onCloseCreateEmbedVideo,
    onOpen: onOpenCreateEmbedVideo,
  } = useDisclosure();

  const {
    isOpen: createEmbedHtmlIsOpen,
    onClose: onCloseCreateEmbedHtml,
    onOpen: onOpenCreateEmbedCode,
  } = useDisclosure();

  // const queryClient = useQueryClient();

  const formSubmit = async (e) => {
    e.preventDefault();

    if (!postMessage.trim()) {
      addToast({ message: 'Please add some text' }, 'warning', 'top');
      return;
    }

    const attachments = [...images];
    let formData = new FormData();
    let media = [
      ...new Set([
        ...(link
          .trim()
          .split(',')
          .forEach((item) => item.trim()) || []),
        ...autoVideos,
      ]),
    ].join(',');
    formData.append('text', postMessage.trim());
    formData.append('media', media);
    if (embedCode) {
      formData.append('embedCode', embedCode);
    }
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i]);
    }

    if (mentions) {
      const actualTaggedUsers = mentions.filter((user) =>
        postMessage.includes(user?.tag),
      );

      for (let i = 0; i < actualTaggedUsers.length; i++) {
        formData.append('mentions', actualTaggedUsers[i]?.id);
      }
    }

    mutate(
      {
        key: 'post',
        method: 'post',
        data: formData,
      },
      {
        onSuccess(res) {
          addToast(res);
          onClose();
          setPostMessage('');
          setTextareaKey((prev) => prev + 1);
          setImages([]);
          setMentions([]);
          setEmbedCode('');
          setAutoVideos([]);
          setLink('');
          // appendToPostList({
          //   ...res.data.post,
          //   user: {
          //     first_name: userInformation.firstName,
          //     last_name: userInformation.lastName,
          //     profile_image: userInformation.profile_image,
          //   },
          // });
          // queryClient.invalidateQueries(`feed?postId=${postId}`);
          refetchFeed();
        },
        onError(e) {
          addToast({ message: 'Post was not created' }, 'error');
        },
      },
    );
  };

  const maxBytes = 3485760;

  const openImageUpload = (e) => {
    imagesInputRef.current.click();
  };

  const handleChangeForImage = (e) => {
    let isTooLarge = [...e.target.files].some((file) => file.size > maxBytes);
    if (isTooLarge) {
      setTimeout(() => {
        addToast({
          message: 'One or more files are greater than 3MB',
          type: 'warning',
        });
      }, 500);
      return;
    }
    if (e.target.files.length < 8) {
      setImages([...e.target.files]);
    } else {
      setTimeout(() => {
        addToast({ message: 'More than 7 files selected', type: 'warning' });
      }, 500);
    }
  };

  const clearLink = (e, index) => {
    let _link = link.split(',').filter((_, i) => i !== index);
    setLink(_link.join(','));
  };

  const clearAutoLink = (e, index) => {
    let _links = autoVideos.filter((_, i) => i !== index);
    setAutoVideos(_links);
  };

  const clearImage = (index) => {
    setImages((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const onAddLink = (value) => {
    setLink(value);
  };

  useEffect(() => {
    setImagePreviews([...images].map((file) => URL.createObjectURL(file)));
  }, [images]);

  // Add embed code handler
  const handleEmbedCode = (code) => {
    if (!code) {
      setEmbedCode('');
      return;
    }

    if (isValidEmbedCode(code)) {
      const responsiveCode = makeEmbedResponsive(code);
      const sanitized = sanitizeHtml(responsiveCode);
      setEmbedCode(sanitized);
    } else {
      addToast({
        message: 'Please enter a valid embed code (iframe, img, or embed div)',
        type: 'warning',
      });
    }
  };

  const onAddCode = (value) => {
    handleEmbedCode(value);
  };

  const handleFocus = () => {
    if (postMessage.trim()) {
      setIsActive(true);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (postAreaRef.current && !postAreaRef.current.contains(event.target)) {
        if (!postMessage.trim()) {
          setIsActive(false);
        }
      }
    }
    const detectedLinks = extractVideoLinks(postMessage);
    setAutoVideos(detectedLinks);

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, postMessage]);

  const handleChange = (e) => {
    setPostMessage(e.target.value);
    if (e.target.value.trim()) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      className="post-creation-area"
      ref={postAreaRef}
    >
      <EmbedVideoLinkModal
        isOpen={createEmbedVideoIsOpen}
        onClose={onCloseCreateEmbedVideo}
        videoLink={link}
        onAddLink={onAddLink}
      />

      <EmbedHtmlCodeModal
        isOpen={createEmbedHtmlIsOpen}
        onClose={onCloseCreateEmbedHtml}
        onAddCode={onAddCode}
      />
      <Box mt={'20px'} className="flex items-start px-3 gap-2">
        <Box>
          <Avatar
            src={userInformation.profile_image || null}
            name={`${userInformation.firstName} ${userInformation.lastName}`}
            size={'md'}
          />
        </Box>
        <Flex
          borderBottomWidth="1px"
          // borderColor="brand.primary"
          alignItems="center"
          className="w-full rounded-3xl bg-gray-200"
        >
          <form className="w-full" onSubmit={formSubmit}>
            <AutoResizeTextarea
              key={textareaKey}
              focusBorderColor="#"
              borderWidth="0px"
              background="transparent"
              variant="outline"
              isRequired={true}
              name="postMessage"
              value={postMessage}
              setValue={setPostMessage}
              mentions={mentions}
              onMentionsChange={setMentions}
              onChange={handleChange}
              onFocus={handleFocus}
              placeholder="What are you thinking...."
              type="postMessage"
              w={'full'}
              maxLength={maxPostLength}
            />

            {/* <UserTaggingTextarea
              key={textareaKey}
              focusBorderColor="#"
              borderWidth="0px"
              variant="outline"
              isRequired={true}
              name="postMessage"
              value={postMessage}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="What are you thinking.... Use @ to tag friends"
              type="postMessage"
              w={'full'}
              maxLength={maxPostLength}
              connectedUsers={connectedUsers || []}
              taggedUsers={taggedUsers}
              onTaggedUsersChange={handleTaggedUsersChange}
            /> */}
          </form>
        </Flex>
      </Box>
      <Box className="flex px-3 justify-end my-1">
        <Text
          className={`text-xs font-semibold ${postMessage.length < maxPostLength ? 'text-gray-600' : 'text-red-800'}`}
        >
          {postMessage.length} / {maxPostLength} characters
        </Text>
      </Box>

      {isActive ? (
        <>
          <Box className={`w-full px-3 min-h-[50px]`}>
            <Box className="flex w-full gap-8 flex-wrap items-center ">
              {images &&
                images.length > 0 &&
                imagePreviews.map((preview, index) => {
                  return (
                    <Box className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-20 h-auto object-cover rounded"
                      />
                      <Box
                        onClick={() => {
                          clearImage(index);
                        }}
                        className="border-2 bg-white absolute top-0 right-[-5px] cursor-pointer border-red-400 rounded-full px-1 grid place-items-center"
                      >
                        <CloseIcon color="red.400" w={2} />
                      </Box>
                    </Box>
                  );
                })}
              {link
                ? link.split(',').map((item, index) => (
                    <Box className="relative">
                      <Box className="w-30 h-auto object-cover rounded">
                        <MediaDisplay media={[item.trim()]} />
                      </Box>
                      <Box
                        onClick={(e) => {
                          clearLink(e, index);
                        }}
                        className="border-2 bg-white absolute top-0 right-[-5px] cursor-pointer border-red-400 rounded-full px-1 grid place-items-center"
                      >
                        <CloseIcon color="red.400" w={2} />
                      </Box>
                    </Box>
                  ))
                : null}

              {autoVideos.map((item, index) => (
                <Box className="relative">
                  <Box className="w-30 h-auto object-cover rounded">
                    <MediaDisplay media={[item.trim()]} />
                  </Box>
                  <Box
                    onClick={(e) => {
                      clearAutoLink(e, index);
                    }}
                    className="border-2 bg-white absolute top-0 right-[-5px] cursor-pointer border-red-400 rounded-full px-1 grid place-items-center"
                  >
                    <CloseIcon color="red.400" w={2} />
                  </Box>
                </Box>
              ))}
              {embedCode ? (
                <Box className="relative">
                  <Box className="w-30 h-auto object-cover rounded">
                    <MediaDisplay media={[embedCode]} />
                  </Box>
                  <Box
                    onClick={() => handleEmbedCode('')}
                    className="border-2 bg-white absolute top-0 right-[-5px] cursor-pointer border-red-400 rounded-full px-1 grid place-items-center"
                  >
                    <CloseIcon color="red.400" w={2} />
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box className="px-3">
            <Box className="flex gap-5 flex-row border rounded-lg my-2 px-3 py-6 items-center justify-between w-full">
            <Text className="text-sm">Add to your post</Text>
            <Flex className="flex items-center gap-2 md:gap-5">
              <Box
                onClick={openImageUpload}
                className="flex items-center gap-1 xs:gap-2 hover:font-medium cursor-pointer"
              >
                <Input
                  accept="image/*"
                  id="image-attachments"
                  type="file"
                  onChange={(e) => handleChangeForImage(e)}
                  name="attachments"
                  key="image-attachments"
                  hidden
                  multiple
                  ref={imagesInputRef}
                />
                <TbPhoto />
                <Text className="text-sm">Image</Text>
              </Box>
              <Box
                onClick={onOpenCreateEmbedVideo}
                className="flex items-center gap-1 xs:gap-2 hover:font-medium cursor-pointer"
              >
                <BsCameraVideo />
                <Text className="text-sm">Video</Text>
              </Box>
              <Box
                onClick={onOpenCreateEmbedCode}
                className="flex items-center gap-1 xs:gap-2 hover:font-medium cursor-pointer"
              >
                <TbCode />
                <Text className="text-sm">Embed</Text>
              </Box>
            </Flex>
          </Box>
          </Box>

            <Flex
              direction={{ base: 'column', sm: 'row' }}
              justify={showCloseButton ? 'space-between' : 'flex-end'}
              className="post-button-container w-full border-t mt-2 py-2 px-3 pt-4"
            >
              {showCloseButton && (
                <BrandButton
                  text="Close"
                  onClick={onClose}
                  w={{ base: 'full', sm: 'auto' }}
                />
              )}
              <BrandButton
                isLoading={isLoading}
                text="Post"
                onClick={formSubmit}
                type="submit"
                variant="md"
                bgColor="brand.primary"
                color="#fff"
                fontSize="14px"
                borderRadius={4}
                w="full"
                _hover={{ background: 'brand.primary' }}
              />
            </Flex>
        </>
      ) : null}
    </Flex>
  );
};
export default CreatePost;
